/**
 * Scene 4 — Architecture Diagram. SLR5500 -> Mini-PC -> AMZEL Cloud.
 *
 * Statisches Diagramm das die End-to-End-Pipeline zeigt mit der zentralen
 * Botschaft: "Audio stays at the edge. Only aggregates leave."
 *
 * Jede Komponente hat einen Tooltip mit kurzer Erklaerung — Daniel kann
 * im Call hoveren und die Details aufklappen.
 *
 * Production-vs-Demo-Mapping wird inline gezeigt — Web Speech API in der
 * Demo, AWS Transcribe/Polly in Production.
 */
import { useState } from "react";
import {
  Brain,
  Cpu,
  Database,
  Ear,
  Eye,
  Headphones,
  Lock,
  Mic,
  Radio,
  RadioTower,
  ShieldCheck,
  Workflow,
} from "lucide-react";

interface ComponentSpec {
  id: string;
  label: string;
  sublabel: string;
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  description: string;
}

const SITE_COMPONENTS: ReadonlyArray<ComponentSpec> = [
  {
    id: "handheld",
    label: "Motorola R7 / DP4800e",
    sublabel: "Handheld radio · PTT-gated mic",
    icon: Radio,
    description:
      "AMBE+2 vocoder on the radio's DSP chip compresses voice to 3.6 kbps. Microphone is OFF until PTT is pressed — no background listening. Includes hardware noise cancellation, VAD, and FEC against bit errors up to 20%.",
  },
  {
    id: "hot-mic",
    label: "Hot Mic on Demand (XCMP)",
    sublabel: "Post-trigger only · 30s auto-stop · LED-visible",
    icon: Eye,
    description:
      "After an acoustic detection, AMZEL can briefly enable the microphone on the 3-4 nearest radios via XCMP control command. Strict constraints: TX-LED stays on (never covert), 30-second auto-stop, audit-logged per device, works only post-detection — never preventive. Lets the affected zone act as a multi-channel acoustic verification array without changing the privacy contract during normal operations.",
  },
  {
    id: "repeater",
    label: "SLR5500 Repeater",
    sublabel: "NAI Voice License active",
    icon: RadioTower,
    description:
      "Network Application Interface license exposes the AMBE+2 voice stream over IP plus call metadata (sender DMR ID, talkgroup, slot, encryption flag, RSSI). One license already purchased for the test repeater.",
  },
  {
    id: "site-sensors",
    label: "Site Acoustic Sensors",
    sublabel: "PoE ceiling mics · 4-8 per zone · always on",
    icon: Ear,
    description:
      "Dedicated ceiling/pillar microphone array, wired via PoE to the Mini-PC — NOT the handheld radios. This is what continuously listens for environmental events (gunshot, scream, alarm). Microphone array geometry enables sub-meter triangulation. Separate from radio audio: PTT-traffic voice-stress and ambient acoustic classification are two independent inputs to the Mini-PC.",
  },
];

const EDGE_COMPONENTS: ReadonlyArray<ComponentSpec> = [
  {
    id: "decoder",
    label: "AMBE+2 Decoder",
    sublabel: "DVSI Software SDK · 3.6 kbps → 16 kHz PCM",
    icon: Workflow,
    description:
      "Licensed DVSI SDK on the Mini-PC decodes the proprietary AMBE+2 stream into standard PCM. This is the only step where audio is in cleartext on disk — and it never leaves this machine.",
  },
  {
    id: "asr",
    label: "Whisper ASR",
    sublabel: "On-device speech recognition · prod = AWS Transcribe",
    icon: Mic,
    description:
      "Whisper-small fine-tuned on warehouse-specific vocabulary. Live transcription per radio session. In production swaps to AWS Transcribe — same API surface, swap is one line of code.",
  },
  {
    id: "scene-classifier",
    label: "Acoustic Scene Classifier",
    sublabel: "YAMNet on hall-sensor streams · not on radio audio",
    icon: Headphones,
    description:
      "TensorFlow YAMNet 521-class classifier runs continuously against the dedicated ceiling-microphone array — NOT against handheld-radio audio (radios stay PTT-gated). Sub-500ms gunshot detection. Multi-mic triangulation locates the event within 2-3 meters.",
  },
  {
    id: "stress",
    label: "Voice Stress Analyser",
    sublabel: "openSMILE on PTT traffic · acoustic biomarkers only",
    icon: Brain,
    description:
      "Runs against regular PTT-triggered radio traffic only — not against any background audio. Extracts pitch, jitter, shimmer, speech-rate. No semantic analysis, no transcription needed. Outputs aggregate stress score per talkgroup, k≥5 anonymised before leaving the Mini-PC.",
  },
  {
    id: "aggregator",
    label: "Aggregator (k≥5)",
    sublabel: "GDPR/BetrVG-compliant pseudonymisation",
    icon: Lock,
    description:
      "Minimum 5 unique speakers per bucket before any metric is emitted. HMAC-hashed speaker IDs with daily-rotating salt. Voice-prints never persist outside the Mini-PC RAM.",
  },
];

const CLOUD_COMPONENTS: ReadonlyArray<ComponentSpec> = [
  {
    id: "ai-module",
    label: "AMZEL AI Module",
    sublabel: "Bedrock Claude Haiku · provider-agnostic",
    icon: Cpu,
    description:
      "Strategy-Pattern abstracts the LLM provider. Today: AWS Bedrock Claude Haiku 4.5. One config flip switches to a tenant's preferred LLM provider (alternate Bedrock model, customer-internal LLM, or on-prem) when production rolls out. Same prompt-engineering pipeline.",
  },
  {
    id: "voice-aggregates",
    label: "Voice Metrics Store",
    sublabel: "TimescaleDB · time-series · k≥5 only",
    icon: Database,
    description:
      "Aggregate-only voice metrics: PTT counts per zone-5min-bucket, stress-score percentiles, encryption-compliance flags. No raw audio, no transcripts, no individual identifiers.",
  },
  {
    id: "audit",
    label: "Audit Trail",
    sublabel: "Immutable · who/what/when/correlation_id",
    icon: ShieldCheck,
    description:
      "Every write event is audit-logged. Any on-demand transcript reveal triggers an audit event with shift-lead approval, Slack notification, and 30-second auto-hide countdown.",
  },
];

export function SceneArchitecture() {
  const [hovered, setHovered] = useState<string | null>(null);

  const hoveredSpec =
    [...SITE_COMPONENTS, ...EDGE_COMPONENTS, ...CLOUD_COMPONENTS].find(
      (c) => c.id === hovered,
    ) ?? null;

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <h2 className="text-xl font-bold tracking-tight">End-to-End architecture</h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Hover any component for details. The vertical line marks the GDPR boundary
          — only k≥5 aggregates cross to the right.
        </p>

        {/* Three-column architecture */}
        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          <Column
            title="Site (Radio Cell)"
            sub="Inside the warehouse building"
            tone="rose"
            components={SITE_COMPONENTS}
            hovered={hovered}
            onHover={setHovered}
          />
          <Column
            title="Mini-PC at Site"
            sub="Edge-resilient · audio stays here"
            tone="violet"
            components={EDGE_COMPONENTS}
            hovered={hovered}
            onHover={setHovered}
          />
          <Column
            title="AMZEL Cloud"
            sub="EU-region · aggregate-only"
            tone="emerald"
            components={CLOUD_COMPONENTS}
            hovered={hovered}
            onHover={setHovered}
          />
        </div>

        {/* Boundary marker */}
        <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-500/30 dark:bg-emerald-500/10">
          <div className="flex items-start gap-3">
            <Lock className="mt-0.5 size-5 shrink-0 text-emerald-700 dark:text-emerald-400" aria-hidden />
            <div>
              <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                GDPR boundary
              </div>
              <p className="mt-1 text-sm text-emerald-900/90 dark:text-emerald-100/90">
                Raw audio never crosses from the Mini-PC to the cloud. Only k≥5
                aggregate metrics and on-demand transcripts (with shift-lead approval
                + audit event) flow to the right. Architecture-level GDPR compliance —
                not policy-level.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Hover detail card */}
      {hoveredSpec && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-md dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-start gap-3">
            <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900">
              <hoveredSpec.icon className="size-5" aria-hidden />
            </div>
            <div className="flex-1">
              <div className="text-base font-semibold tracking-tight">
                {hoveredSpec.label}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {hoveredSpec.sublabel}
              </div>
              <p className="mt-2 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                {hoveredSpec.description}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

const COLUMN_TONES = {
  rose: {
    border: "border-rose-200 dark:border-rose-500/30",
    bg: "bg-rose-50/50 dark:bg-rose-500/5",
    title: "text-rose-900 dark:text-rose-200",
    sub: "text-rose-700 dark:text-rose-400",
    iconBg: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  },
  violet: {
    border: "border-violet-200 dark:border-violet-500/30",
    bg: "bg-violet-50/50 dark:bg-violet-500/5",
    title: "text-violet-900 dark:text-violet-200",
    sub: "text-violet-700 dark:text-violet-400",
    iconBg: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
  },
  emerald: {
    border: "border-emerald-200 dark:border-emerald-500/30",
    bg: "bg-emerald-50/50 dark:bg-emerald-500/5",
    title: "text-emerald-900 dark:text-emerald-200",
    sub: "text-emerald-700 dark:text-emerald-400",
    iconBg: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  },
} as const;

function Column({
  title, sub, tone, components, hovered, onHover,
}: Readonly<{
  title: string;
  sub: string;
  tone: keyof typeof COLUMN_TONES;
  components: ReadonlyArray<ComponentSpec>;
  hovered: string | null;
  onHover: (id: string | null) => void;
}>) {
  const t = COLUMN_TONES[tone];
  return (
    <div className={`rounded-2xl border ${t.border} ${t.bg} p-4`}>
      <h3 className={`text-sm font-semibold tracking-tight ${t.title}`}>{title}</h3>
      <p className={`mt-0.5 text-xs ${t.sub}`}>{sub}</p>

      <ul className="mt-4 space-y-2">
        {components.map((c) => {
          const isHovered = hovered === c.id;
          const Icon = c.icon;
          return (
            <li key={c.id}>
              <button
                type="button"
                onMouseEnter={() => onHover(c.id)}
                onMouseLeave={() => onHover(null)}
                onFocus={() => onHover(c.id)}
                onBlur={() => onHover(null)}
                className={`flex w-full items-center gap-3 rounded-xl border bg-white p-3 text-left transition-all dark:bg-slate-900 ${
                  isHovered
                    ? "border-slate-400 shadow-md dark:border-slate-500"
                    : "border-slate-200 hover:border-slate-300 dark:border-slate-800 dark:hover:border-slate-700"
                }`}
              >
                <div className={`grid size-9 shrink-0 place-items-center rounded-lg ${t.iconBg}`}>
                  <Icon className="size-4" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{c.label}</div>
                  <div className="truncate text-[11px] text-slate-500 dark:text-slate-400">
                    {c.sublabel}
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
